import { FORM_IDS } from "constants/v3/formConstants";
import { MEMBER_ACCOUNT_ACTIVE_STATUSES } from "constants/v3/generalConstants";
import { GL_PROGRAM_GENOMIC_LIFE_COMPLETE_DIRECT } from "constants/genomicLifeCompleteProgramCopy";

const AGREEMENT_FORM_IDS = Object.values(FORM_IDS);

export function findValueByKey(obj: unknown, key: string): unknown | null {
  if (obj == null || typeof obj !== "object") {
    return null;
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findValueByKey(item, key);
      if (found != null) return found;
    }
    return null;
  }
  const record = obj as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, key)) {
    return record[key];
  }
  for (const value of Object.values(record)) {
    const found = findValueByKey(value, key);
    if (found != null) return found;
  }
  return null;
}

/**
 * True when the Auth0 ID token includes a non-empty `glMemberStatus` claim.
 * Unpaid-invitee sessions omit this claim until a plan status exists in the identity provider.
 */
export function idTokenHasGlMemberStatus(claims: unknown): boolean {
  const raw = findValueByKey(claims, "glMemberStatus");
  if (raw == null) return false;
  if (typeof raw === "string") return raw.trim() !== "";
  return true;
}

export function getIsMemberActiveFromIdTokenClaims(claims: unknown): boolean {
  const raw = findValueByKey(claims, "glMemberStatus");
  const glMemberStatus = typeof raw === "string" ? raw : "";
  return MEMBER_ACCOUNT_ACTIVE_STATUSES.includes(
    glMemberStatus.replace(/[,/\-\s']/g, "").toUpperCase()
  );
}

export function getGlMemberStatusFromIdTokenClaims(
  claims: unknown
): string | null {
  const raw = findValueByKey(claims, "glMemberStatus");
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

function getBooleanClaimFromIdToken(claims: unknown, key: string): boolean {
  const raw = findValueByKey(claims, key);
  if (raw === true) {
    return true;
  }
  if (raw === false) {
    return false;
  }
  if (typeof raw === "string") {
    const normalized = raw.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

export type ShareEligibilityFromIdToken = {
  eligibleToGift: boolean;
  eligibleToInvite: boolean;
};

export function getShareEligibilityFromIdTokenClaims(
  claims: unknown
): ShareEligibilityFromIdToken {
  return {
    eligibleToGift: getBooleanClaimFromIdToken(claims, "eligibleToGift"),
    eligibleToInvite: getBooleanClaimFromIdToken(claims, "eligibleToInvite")
  };
}

function resolveGlProgramId(claims: unknown): string | null {
  const raw = findValueByKey(claims, "glProgramId");
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

type AgreementSubmissionLike = {
  formInfo?: { id?: string | null } | null;
};

export function memberNeedsAgreementOnboarding(
  submissions?: AgreementSubmissionLike[] | null
): boolean {
  if (!submissions?.length) {
    return true;
  }

  const submittedFormIds = new Set(
    submissions
      .map((submission) => submission.formInfo?.id)
      .filter((formId): formId is string => Boolean(formId?.trim()))
  );

  return AGREEMENT_FORM_IDS.some((formId) => !submittedFormIds.has(formId));
}

export function memberHasDateOfBirth(dob: unknown): boolean {
  if (typeof dob !== "string") {
    return false;
  }

  return dob.trim() !== "";
}

type DirectInviteeMemberContext = {
  needsAgreementOnboarding?: boolean;
  hasDateOfBirth?: boolean;
};

export function isDirectAccessInvitee(claims: unknown): boolean {
  const programId = resolveGlProgramId(claims);

  if (programId === GL_PROGRAM_GENOMIC_LIFE_COMPLETE_DIRECT) {
    return true;
  }

  if (programId != null) {
    return false;
  }

  if (idTokenHasGlMemberStatus(claims)) {
    return false;
  }

  return true;
}

function isDirectInviteeOnboarding(
  claims: unknown,
  member: DirectInviteeMemberContext = {}
): boolean {
  if (!isDirectAccessInvitee(claims)) {
    return false;
  }

  const needsAgreements = member.needsAgreementOnboarding ?? true;
  const needsDob = member.hasDateOfBirth === false;

  // A direct invitee without any plan status has not paid yet, so onboarding
  // remains incomplete even once agreements and DOB are done.
  return needsAgreements || needsDob || directInviteeRequiresPayment(claims);
}

function directInviteeRequiresPayment(claims: unknown): boolean {
  return !idTokenHasGlMemberStatus(claims);
}

export type DirectInviteeUserState = {
  glProgramId?: string | null;
  glMemberStatus?: string | null;
  isMemberActive?: boolean | null;
  dob?: string | null;
};

/**
 * True when Redux user state shows any plan status at all. A direct invitee
 * with no status has never paid; a present-but-inactive status means a paid
 * (possibly lapsed) plan.
 */
export function userStateHasGlMemberStatus(
  user: DirectInviteeUserState
): boolean {
  if (
    typeof user.glMemberStatus === "string" &&
    user.glMemberStatus.trim() !== ""
  ) {
    return true;
  }

  return user.isMemberActive === true;
}

/** Mirrors {@link isDirectAccessInvitee} for Redux-derived user state. */
export function isDirectInviteeFromUserState(
  user: DirectInviteeUserState
): boolean {
  if (user.glProgramId === GL_PROGRAM_GENOMIC_LIFE_COMPLETE_DIRECT) {
    return true;
  }

  if (user.glProgramId != null && String(user.glProgramId).trim() !== "") {
    return false;
  }

  if (userStateHasGlMemberStatus(user)) {
    return false;
  }

  return true;
}

export function resolveDirectInviteeOnboardingPath(
  claims: unknown,
  member: DirectInviteeMemberContext = {}
): "product" | "profile" | "standard" {
  if (!isDirectInviteeOnboarding(claims, member)) {
    return "standard";
  }

  const needsAgreements = member.needsAgreementOnboarding ?? true;

  if (directInviteeRequiresPayment(claims) && needsAgreements) {
    return "product";
  }

  return "profile";
}

export function directInviteeNeedsOnboardingProfileRedirect(
  user: DirectInviteeUserState,
  agreementOnboardingIncomplete: boolean
): boolean {
  if (!isDirectInviteeFromUserState(user)) {
    return false;
  }

  // No status at all means payment is still outstanding; keep the member in
  // invitee onboarding so they can review their profile and pay.
  const paymentOutstanding = !userStateHasGlMemberStatus(user);

  return (
    agreementOnboardingIncomplete ||
    !memberHasDateOfBirth(user.dob) ||
    paymentOutstanding
  );
}

const GL_MEMBER_ID_POLL_TIMEOUT_MS = 15000;
const GL_MEMBER_ID_POLL_INTERVAL_MS = 500;

type WaitForGlMemberIdOptions = {
  timeoutMs?: number;
  pollIntervalMs?: number;
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * After access-code lookup, Auth0 may not include `glMemberId` in the ID token
 * immediately. Poll a fresh token until the claim appears or timeout.
 */
export async function waitForGlMemberIdInIdToken(
  getAccessTokenSilently: (options?: {
    cacheMode?: "on" | "off" | "cache-only";
  }) => Promise<string>,
  getIdTokenClaims: () => Promise<unknown>,
  options: WaitForGlMemberIdOptions = {}
): Promise<{ glMemberId: string; accessToken: string }> {
  const timeoutMs = options.timeoutMs ?? GL_MEMBER_ID_POLL_TIMEOUT_MS;
  const pollIntervalMs =
    options.pollIntervalMs ?? GL_MEMBER_ID_POLL_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const accessToken = await getAccessTokenSilently({ cacheMode: "off" });
    const claims = await getIdTokenClaims();
    const glMemberId = findValueByKey(claims, "glMemberId");

    if (typeof glMemberId === "string" && glMemberId.trim() !== "") {
      return { glMemberId: glMemberId.trim(), accessToken };
    }

    await sleep(pollIntervalMs);
  }

  throw new Error("Timed out waiting for glMemberId in ID token");
}
